/**
 * InlineCostBadge — pill-style credit-cost badge near paid-action buttons.
 *
 * Visual style matches existing TokenBadge (compact mode) for UX consistency:
 *   - Green pill + check icon when credits known
 *   - Slate pill + spinner when computing
 *   - Red pill + X icon when error
 *
 * Number format: full thousand-separator (e.g. "14,152 credits"), NOT compact "~14K".
 */

import React from 'react';
import { tokensToPages } from '../../services/mock/creditMock';

interface Props {
    credits?: number | null;
    isComputing?: boolean;
    error?: string | null;
    /** @deprecated Always shows full "credits" label now (US-04-10 consistency with TokenBadge). */
    compact?: boolean;
    className?: string;
}

const fmt = (n: number) => n.toLocaleString('en-US');

const CheckIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
);

const SpinIcon = () => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ animation: 'spin 1s linear infinite' }}>
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
);

const ErrorIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="15" y1="9" x2="9" y2="15" />
        <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
);

const PILL_BASE: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    borderRadius: '9999px',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    fontWeight: 600,
};

const InlineCostBadge: React.FC<Props> = ({ credits, isComputing, error, className = '' }) => {
    if (error) {
        return (
            <span
                className={className}
                style={{
                    ...PILL_BASE,
                    color: '#991b1b',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                }}
                title={`Không tính được cost: ${error}`}
            >
                <ErrorIcon />
                <span style={{ fontSize: 11 }}>lỗi đếm</span>
            </span>
        );
    }
    if (isComputing) {
        return (
            <span
                className={className}
                style={{
                    ...PILL_BASE,
                    color: '#64748b',
                    backgroundColor: '#f1f5f9',
                    border: '1px solid #e2e8f0',
                    fontWeight: 500,
                }}
                title="Đang tính toán credits…"
            >
                <SpinIcon />
                <span style={{ fontSize: 11 }}>tính toán…</span>
            </span>
        );
    }
    if (typeof credits !== 'number' || credits <= 0) {
        // Idle state — render như counting để consistent UX (đang chờ bundle)
        return (
            <span
                className={className}
                style={{
                    ...PILL_BASE,
                    color: '#64748b',
                    backgroundColor: '#f1f5f9',
                    border: '1px solid #e2e8f0',
                    fontWeight: 500,
                }}
                title="Đang tính credits"
            >
                <SpinIcon />
                <span style={{ fontSize: 11 }}>tính toán…</span>
            </span>
        );
    }
    const pages = tokensToPages(credits);
    return (
        <span
            className={className}
            style={{
                ...PILL_BASE,
                color: '#065f46',
                backgroundColor: '#ecfdf5',
                border: '1px solid #a7f3d0',
            }}
            title={`${fmt(credits)} credits (~${pages} trang)`}
        >
            <CheckIcon />
            <span>{fmt(credits)}</span>
            <span style={{ opacity: 0.6, fontSize: 10, fontWeight: 400 }}>credits</span>
        </span>
    );
};

export default InlineCostBadge;
